/**
 * TimelineItemActions component
 *
 * Displays action buttons and checkbox for timeline items in edit mode
 * when the item is selected.
 */

import { clsx } from "clsx";
import { IconButton } from "../../IconButton";
import { TimelineItemAction } from "../types";

export interface TimelineItemActionsProps {
  /**
   * Whether the checkbox is checked (independent of selected state)
   */
  checked?: boolean;

  /**
   * Whether the item is marked for creation (newly added, not published)
   */
  markedForCreation?: boolean;

  /**
   * Whether the item is marked for deletion (soft delete)
   */
  markedForDeletion?: boolean;

  /**
   * Whether the item is starred
   */
  isStarred?: boolean;

  /**
   * Whether the item is marked important (effective value — explicit user
   * annotation or record-type default). Drives the filled/colored state of
   * the "Important" toggle.
   */
  isImportant?: boolean;

  /**
   * Callback when an action button is clicked
   */
  onActionClick: (action: TimelineItemAction) => void;

  /**
   * Whether the rerun button should be rendered. Callers use this to gate
   * behind a feature flag and to hide the button while the item is mid-rerun.
   * Defaults to true.
   */
  showRerunAction?: boolean;
  /**
   * Tooltip text for the rerun button. Lets callers gate the wording
   * behind a feature flag (e.g. "Split and resummarization" vs. "Rerun
   * summarization"). Defaults to "Rerun summarization".
   */
  rerunTooltip?: string;
  /**
   * Whether the "File a ticket" button should be rendered. Callers gate
   * this on user role (hidden for doctors). Defaults to true.
   */
  showFileTicketAction?: boolean;
  /**
   * Whether the "View OCR text" button should be rendered. Callers gate
   * this on file type (e.g. hidden for sub-rosa video entries). Defaults
   * to true.
   */
  showOcrTextAction?: boolean;
  /**
   * Whether the "Open PDF" button should be rendered. Callers gate this
   * on record type (e.g. hidden for sub-rosa video entries — the
   * previewer only handles PDFs). Defaults to true.
   */
  showPdfAction?: boolean;
}

/**
 * TimelineItemActions component
 *
 * Renders a vertical panel with checkbox and action icons
 */
export const TimelineItemActions: React.FC<TimelineItemActionsProps> = ({
  markedForCreation = false,
  markedForDeletion = false,
  isStarred = false,
  isImportant = false,
  onActionClick,
  showRerunAction = true,
  rerunTooltip = "Rerun summarization",
  showFileTicketAction = true,
  showOcrTextAction = true,
  showPdfAction = true,
}) => {
  return (
    <div className="indigo-timeline-item__actions">
      <div className="indigo-timeline-item__actions-content">
        {/* Action buttons */}
        <div className="indigo-timeline-item__action-buttons">
          <IconButton
            iconName={isStarred ? "starred" : "star"}
            variant="ghost"
            size="md"
            tooltip={
              isStarred ? (
                <span>
                  Un<u>s</u>tar
                </span>
              ) : (
                <span>
                  <u>S</u>tar
                </span>
              )
            }
            onClick={(e) => {
              e.stopPropagation();
              onActionClick("star");
            }}
          />

          <IconButton
            className={clsx(
              "indigo-timeline-item__important-btn",
              isImportant && "is-important",
            )}
            iconName="lightbulb"
            variant="ghost"
            size="md"
            tooltip={isImportant ? "Unmark important" : "Mark important"}
            onClick={(e) => {
              e.stopPropagation();
              onActionClick("important");
            }}
          />

          <IconButton
            iconName="link"
            variant="ghost"
            size="md"
            tooltip={
              <span>
                <u>L</u>ink
              </span>
            }
            onClick={(e) => {
              e.stopPropagation();
              onActionClick("link");
            }}
          />

          {showPdfAction && (
            <IconButton
              iconName="pdf"
              variant="ghost"
              size="md"
              tooltip="Open PDF"
              onClick={(e) => {
                e.stopPropagation();
                onActionClick("pdf");
              }}
            />
          )}
          {showOcrTextAction && (
            <IconButton
              iconName="sticky-note"
              variant="ghost"
              size="md"
              tooltip="View OCR text"
              onClick={(e) => {
                e.stopPropagation();
                onActionClick("ocrText");
              }}
            />
          )}
          <IconButton
            iconName="undo"
            variant="ghost"
            size="md"
            tooltip={
              <span>
                <u>R</u>eset
              </span>
            }
            onClick={(e) => {
              e.stopPropagation();
              onActionClick("reset");
            }}
            disabled={markedForCreation}
          />

          {showRerunAction && (
            // Intentionally does NOT disable on `markedForCreation`. A newly
            // created entry still pinpoints a specific (document, page range)
            // tuple, so re-summarizing is meaningful — the backend will just
            // overwrite the draft in place with the new LLM output.
            // Only `markedForDeletion` blocks rerun (no point running the
            // LLM on an entry that's about to be deleted on publish).
            <IconButton
              iconName="refresh"
              variant="ghost"
              size="md"
              tooltip={
                markedForDeletion
                  ? "Cannot rerun: entry is marked for deletion"
                  : rerunTooltip
              }
              disabled={markedForDeletion}
              onClick={(e) => {
                e.stopPropagation();
                onActionClick("rerun");
              }}
            />
          )}

          <IconButton
            iconName="trash"
            variant="danger"
            size="md"
            tooltip={
              <span>
                <u>D</u>elete
              </span>
            }
            disabled={markedForDeletion}
            onClick={(e) => {
              e.stopPropagation();
              onActionClick("delete");
            }}
          />

          {showFileTicketAction && (
            <IconButton
              iconName="bug"
              variant="ghost"
              size="md"
              tooltip="Report bug"
              onClick={(e) => {
                e.stopPropagation();
                onActionClick("fileTicket");
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
